import React, { useMemo, useCallback, useEffect } from 'react';
import { Plan } from 'src/features/onboarding/components/7-finish/interfaces';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useInsuranceStore } from 'src/store/insurance';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { format, parseISO } from 'date-fns';
import {
  IDENTITY_PROTECTION_PLANS,
  LEGAL_PLANS,
  TELEHEALTH_PLANS,
} from '@features/onboarding/components/5-beyond-health-form/constants';
import { calculateCoverageLevel } from '@features/onboarding/utils/coverageLevel';
import { getCriticalIllnessPriceId, PriceSearchConditions } from '@features/onboarding/utils/criticalIllnessPriceId';

export const usePlan = () => {
  const { handleJumpTo } = useFormControls();
  const { underwritingResult } = useFinalSubmissionStore();

  const { data, setLastSelectedPHQ } = useOnboardingStore();
  const plansFromStore = useInsuranceStore((state) => state.plans);
  const providersFromStore = useInsuranceStore((state) => state.providers);

  const setData = useOnboardingStore((state) => state.setData);
  const sanitizeCategory = (category: string) =>
    category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const getPlanPrice = (
    plan: InsurancePlan,
    coverageLevel: number,
    selectedTier?: string,
    coverageAmount?: number
  ) => {
    if (!plan.prices || plan.prices.length === 0) {
      return 0;
    }

    if (plan.category === 'Critical Illness') {
      const price = calculateCriticalIllnessPrice(plan, coverageLevel, coverageAmount);
      return price;
    }

    let availablePrices = plan.prices.filter(
      (p) => p.coverage === coverageLevel
    );

    if (selectedTier && availablePrices.length > 1) {
      availablePrices = availablePrices.filter((p) => p.tier === selectedTier);
    }

    if (!selectedTier && availablePrices.length > 1) {
      availablePrices = availablePrices.filter((p: any) => p.tier === 'Tier 1');
    }

    const finalPrice = availablePrices[0]?.price ?? 0;
    return finalPrice;
  };

  const calculateCriticalIllnessPrice = (
    plan: InsurancePlan,
    coverageLevel: number,
    coverageAmount?: number
  ): number => {
    const selectedCoverageAmount = +(
      coverageAmount ||
      plan.details?.coverageAmount ||
      10000
    );

    let hasSpouse = false;
    let dependentCount = 0;

    if (plan.category === 'Major Medical') {
      const medicalTags = data.medicalTags || [];
      hasSpouse = medicalTags.some((tag: string) => tag.includes('spouse'));
      dependentCount = medicalTags.filter((tag: string) =>
        tag.includes('dependent')
      ).length;
    } else {
      const planTags = data.planTags || {};
      const planTagsForThisPlan = planTags[plan.uid] || [];
      hasSpouse = planTagsForThisPlan.some((tag: string) =>
        tag.includes('spouse')
      );
      dependentCount = planTagsForThisPlan.filter((tag: string) =>
        tag.includes('dependent')
      ).length;
    }

    let primaryAge = 30;
    if (data.applicants && data.applicants.length > 0) {
      const primaryApplicant = data.applicants.find(
        (app) => app.applicant === 'primary'
      );
      if (primaryApplicant && primaryApplicant.dateOfBirth) {
        const birthYear = parseInt(primaryApplicant.dateOfBirth.year);
        const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
        primaryAge = currentYear - birthYear;
      }
    }

    const planTags = data.planTags || {};
    const planTagsForThisPlan = planTags[plan.uid] || [];

    const conditions: PriceSearchConditions = {
      coverageAmount: selectedCoverageAmount,
      primaryAge,
      dependentsCount: dependentCount,
      hasSpouse,
    };

    const priceId = getCriticalIllnessPriceId(plan, conditions);
    const validPrice = plan.prices.find(price => price.id === priceId);

    const finalPrice = validPrice?.price || 0;

    return finalPrice;
  };

  const getPlanInfo = useCallback(
    (plan: InsurancePlan, planUid: string) => {
      let coverageLevel: number;
      if (plan.category === 'Major Medical') {
        const medicalTags = data.medicalTags || [];
        coverageLevel = calculateCoverageLevel(medicalTags);
      } else {
        const planTags = data.planTags || {};
        const planTagsForThisPlan = planTags[planUid] || [];
        coverageLevel = calculateCoverageLevel(planTagsForThisPlan);
      }

      let selectedTier: string | undefined;
      let selectedCoverageAmount: number | undefined;

      if (plan.category === 'Major Medical') {
        if (data.medicalPlanTier) {
          selectedTier = data.medicalPlanTier;
        }
        if (data.medicalPlanCoverageAmount) {
          selectedCoverageAmount = data.medicalPlanCoverageAmount;
        }
      } else if (plan.category === 'Dental') {
        if (data.dentalPlanTier) {
          selectedTier = data.dentalPlanTier;
        }
        if (data.dentalPlanCoverageAmount) {
          selectedCoverageAmount = data.dentalPlanCoverageAmount;
        }
      } else if (plan.category === 'Critical Illness') {
        const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
        const coverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
        const coverageAmount = (data as Record<string, unknown>)[
          coverageAmountKey
        ] as number | undefined;
        if (coverageAmount) {
          selectedCoverageAmount = coverageAmount;
        }
      }

      const price = getPlanPrice(
        plan,
        coverageLevel,
        selectedTier,
        selectedCoverageAmount
      );

      return {
        coverageLevel,
        selectedTier,
        selectedCoverageAmount,
        price,
      };
    },
    [data, calculateCoverageLevel, getPlanPrice]
  );

  const plans = React.useMemo(() => {
    const selectedPlans: (Plan & { baseKey: string })[] = [];

    const selectedPlanUids = Object.entries(data)
      .filter(
        ([key]) =>
          key.endsWith('Plan') &&
          !key.endsWith('PlanPrice') &&
          !key.endsWith('PlansToCompare')
      )
      .map(([, value]) => value)
      .filter(Boolean) as string[];

    selectedPlanUids.forEach((planUid) => {
      const plan = plansFromStore.find((p) => p.uid === planUid);
      if (plan) {
        let categoryKey = sanitizeCategory(plan.category || '');
        const planKey = `${categoryKey}Plan`;

        const planInfo = getPlanInfo(plan, planUid);

        const providerName =
          providersFromStore.find((pr) => {
            return (
              pr.uid === String(plan.providerId) ||
              (pr as any).id === plan.providerId
            );
          })?.name || plan.category;

        selectedPlans.push({
          insurer: providerName,
          name: plan.name || plan.marketingName || 'Plan',
          price: planInfo.price,
          baseKey: categoryKey,
        });
      }
    });

    if (data.identityProtectionPlan) {
      const identityPlan = IDENTITY_PROTECTION_PLANS.find(
        (p) => p.id === data.identityProtectionPlan
      );
      if (identityPlan) {
        selectedPlans.push({
          insurer: 'LifeLock',
          name: identityPlan.title,
          price: parseFloat(identityPlan.price.replace('$', '') || '0'),
          baseKey: 'identityProtection',
        });
      }
    }

    if (data.legalPlan) {
      const legalPlan = LEGAL_PLANS.find((p) => p.id === data.legalPlan);
      if (legalPlan) {
        selectedPlans.push({
          insurer: 'LegalShield',
          name: legalPlan.title,
          price: parseFloat(legalPlan.price.replace('$', '') || '0'),
          baseKey: 'legal',
        });
      }
    }

    if (data.telehealthPlan) {
      const telehealthPlan = TELEHEALTH_PLANS.find(
        (p) => p.id === data.telehealthPlan
      );
      if (telehealthPlan) {
        selectedPlans.push({
          insurer: 'Doctegrity',
          name: telehealthPlan.title,
          price: parseFloat(telehealthPlan.price.replace('$', '') || '0'),
          baseKey: 'telehealth',
        });
      }
    }

    return selectedPlans;
  }, [data, plansFromStore, providersFromStore, getPlanInfo]);

  const { setLastSelectedExtraPlan } = useOnboardingStore.getState();

  const handleChangePlan = useCallback(
    (plan: Plan & { baseKey?: string }) => {
      let key = (plan as any).baseKey as string | undefined;
      if (!key) return;

      if (key === 'majormedical') key = 'medical';

      setLastSelectedPHQ(false);
      if (key === 'medical') {
        setLastSelectedExtraPlan(null);
        handleJumpTo(FormStepSections.Medical);
      } else {
        setLastSelectedExtraPlan(key);
        handleJumpTo(FormStepSections.Dental);
      }
    },
    [handleJumpTo]
  );

  const handleRemovePlan = useCallback(
    (plan: Plan & { baseKey?: string }) => {
      let baseKey = (plan as any).baseKey as string | undefined;
      if (baseKey === 'majormedical') {
        baseKey = 'medical';
      }
      if (!baseKey) return;

      const planKey = `${baseKey}Plan`;
      const compareKey = `${baseKey}PlansToCompare`;
      const priceKey = `${baseKey}PlanPrice`;
      const tierKey = `${baseKey}PlanTier`;
      const coverageAmountKey = `${baseKey}PlanCoverageAmount`;

      const resetData: Record<string, unknown> = {
        [planKey]: '',
        [compareKey]: [],
        [priceKey]: 0,
        [tierKey]: '',
      };

      const planUid = (data as Record<string, unknown>)[planKey] as string;
      if (planUid) {
        const plan = plansFromStore.find((p) => p.uid === planUid);
        if (plan?.category === 'Critical Illness') {
          resetData[coverageAmountKey] = 10000;
        } else {
          resetData[coverageAmountKey] = null;
        }
      } else {
        resetData[coverageAmountKey] = null;
      }

      setData(resetData as any);

      if (plans.length === 1) {
        handleJumpTo(FormStepSections.Finish);
      }
    },
    [setData, plans.length, handleJumpTo, data, plansFromStore]
  );

  const totalPlanPrice = useMemo(() => {
    let total = 0;

    const selectedPlanUids = Object.entries(data)
      .filter(
        ([key]) =>
          key.endsWith('Plan') &&
          !key.endsWith('PlanPrice') &&
          !key.endsWith('PlansToCompare')
      )
      .map(([, value]) => value)
      .filter(Boolean) as string[];

    selectedPlanUids.forEach((planUid) => {
      const plan = plansFromStore.find((p) => p.uid === planUid);
      if (plan) {
        if (
          plan.category === 'Major Medical' &&
          underwritingResult?.status === 'rejected'
        ) {
          return;
        }
        const planInfo = getPlanInfo(plan, planUid);
        total += planInfo.price;
      }
    });

    if (data.identityProtectionPlan) {
      const identityPlan = IDENTITY_PROTECTION_PLANS.find(
        (p) => p.id === data.identityProtectionPlan
      );
      if (identityPlan) {
        total += parseFloat(identityPlan.price.replace('$', '') || '0');
      }
    }

    if (data.legalPlan) {
      const legalPlan = LEGAL_PLANS.find((p) => p.id === data.legalPlan);
      if (legalPlan) {
        total += parseFloat(legalPlan.price.replace('$', '') || '0');
      }
    }

    if (data.telehealthPlan) {
      const telehealthPlan = TELEHEALTH_PLANS.find(
        (p) => p.id === data.telehealthPlan
      );
      if (telehealthPlan) {
        total += parseFloat(telehealthPlan.price.replace('$', '') || '0');
      }
    }
    return total;
  }, [data, plansFromStore, getPlanInfo, underwritingResult]);

  useEffect(() => {
    console.log('[usePlan] Data changed:', {
      medicalTags: data.medicalTags,
      planTags: data.planTags,
      totalPlanPrice,
    });
  }, [data.medicalTags, data.planTags, totalPlanPrice]);

  return {
    totalPlanPrice,
    plans,
    handleChangePlan,
    calculateCoverageLevel,
    sanitizeCategory,
    handleRemovePlan,
    getPlanPrice,
  };
};
